/**
 * 인증 API 파사드(spec 028 FR-010~017) — 화면은 이 모듈의 함수 시그니처만 의존한다.
 * 계약·선례: specs/002(가입·이메일 인증)·004(비밀번호 재설정), `frontend-fo/src/api/{auth,password}.ts`.
 *
 * 기대된 실패는 무음으로 넘기고 화면이 인라인으로 처리한다 — 로그인 401·PENDING 403·429, 인증·재설정 400, 쿨다운 429, 핸드오프 404.
 */
import { ApiError, http, isApiStatus, withErrors } from './http';
import {
  toMemberAxis,
  toWorkspaceFeatures,
  type MemberAxis,
  type WorkspaceFeature,
} from '@/config/workspace-features';

export { ApiError, isApiStatus };

export interface SessionInfo {
  authenticated: boolean;
  email: string | null;
  name: string | null;
  roles: readonly string[];
  /** 워크스페이스 사용자 유형(축) — 회원이 아니면 null(specs/064). */
  axis: MemberAxis | null;
  /** 쓸 수 있는 워크스페이스 기능 — 화면 진입·메뉴 판단의 근거(specs/064). */
  features: readonly WorkspaceFeature[];
}

export interface LocalLoginRequest {
  /** 로그인 아이디(username) — 화면 호환을 위해 필드명만 email 로 유지. */
  email: string;
  password: string;
  /** "로그인 상태 유지"(specs/048) — true 면 장기 세션(서버 정책 소유). 생략 시 기본 세션. */
  rememberMe?: boolean;
}

/** 가입 동의 묶음 — 로컬·소셜 공통(백엔드 SignupAgreements). */
export interface SignupAgreements {
  age14: boolean;
  terms: boolean;
  privacy: boolean;
  marketing: boolean;
}

/** 아이디/비밀번호 가입 요청(백엔드 LocalSignupRequest). */
export interface LocalSignupRequest {
  username: string;
  password: string;
  passwordConfirm: string;
  email: string;
  /** 휴대전화(선택, specs/080) — 하이픈 허용, 서버가 숫자만 저장. */
  phone?: string;
  agreements: SignupAgreements;
  /** 가입 회원 유형(specs/065) — 화면은 항상 보낸다. */
  memberAxis?: MemberAxis;
}

/** 소셜 로그인 provider — 백엔드 경로 세그먼트(`/authorize/{provider}`)와 같은 소문자 코드. */
export type SocialProvider = 'google' | 'kakao';

/**
 * 소셜 가입 요청(백엔드 SocialSignupRequest, specs/033) — 비밀번호는 받지 않는다.
 * `email` 은 provider 가 검증 이메일을 주지 않은 경우에만 필수(그 외 서버가 무시하고 핸드오프 이메일을 쓴다).
 */
export interface SocialSignupRequest {
  handoffToken: string;
  email?: string;
  /** 휴대전화(선택, specs/080). */
  phone?: string;
  agreements: SignupAgreements;
  memberAxis?: MemberAxis;
}

/** 소셜 가입 핸드오프 표시 컨텍스트(비소진 peek). `emailVerified` 면 이메일 readonly, 아니면 입력(값은 프리필). */
export interface HandoffContext {
  provider: 'GOOGLE' | 'KAKAO';
  email: string | null;
  emailVerified: boolean;
  nickname: string | null;
  channel: string;
}

/** 소셜 가입 결과 — VERIFIED 면 세션이 발급됐고, PENDING 이면 인증 메일을 보낸 상태(세션 없음). */
export type SocialSignupResult =
  { status: 'VERIFIED'; session: SessionInfo } | { status: 'PENDING'; email: string };

/** 가입 응답(SignupResponse) — 로컬·소셜 공통. `authStatus` 로 세션 발급 여부가 갈린다. */
export interface SignupResponse {
  username: string;
  email: string;
  authStatus: 'PENDING' | 'VERIFIED';
}

/** 인증 대기 정보(PendingVerificationResponse) — 인증 메일이 간 현재 이메일. */
export interface PendingVerificationResponse {
  email: string;
}

/** 인증 대기 이메일 정정 요청(ChangePendingEmailRequest). */
export interface ChangePendingEmailRequest {
  email: string;
}

const GUEST: SessionInfo = {
  authenticated: false,
  email: null,
  name: null,
  roles: [],
  axis: null,
  features: [],
};

/** 서버 세션 확인 응답(FoSessionResponse) — 화면은 이를 `SessionInfo` 로 바꿔 쓴다. */
export interface SessionResponse {
  authenticated: boolean;
  email: string | null;
  /** 표시 이름(specs/053) */
  name: string | null;
  roles: string[] | null;
  axis: MemberAxis | null;
  features: WorkspaceFeature[] | null;
}

/** 세션 확인 — 200 → 인증 세션, 401 → guest(무음·정상 흐름, FR-011). */
export async function fetchSession(): Promise<SessionInfo> {
  try {
    const { data } = await http.get<SessionResponse>(
      '/fo/auth/oauth2/me',
      withErrors({ silentStatuses: [401] }),
    );
    return data.authenticated
      ? {
          authenticated: true,
          email: data.email,
          name: data.name,
          roles: data.roles ?? [],
          axis: toMemberAxis(data.axis),
          features: toWorkspaceFeatures(data.features),
        }
      : GUEST;
  } catch (error) {
    if (isApiStatus(error, 401)) return GUEST;
    throw error;
  }
}

/** 이메일 미인증(PENDING) 로그인 403 의 서버 메시지 마커 — 채널 불일치 등 다른 403 과 구분한다(frontend-fo 와 동일). */
const PENDING_LOGIN_MESSAGE_MARKER = '이메일 인증이 필요합니다';

/**
 * 로컬 로그인(FR-010) — 성공 시 세션 확인으로 SessionInfo 구성.
 * 401(자격 불일치)·429(잠금)·PENDING 403 은 화면이 인라인 처리하므로 무음, 그 외 403(채널 불일치 등)은 서버 사유를 토스트로 노출한다.
 */
export async function login(req: LocalLoginRequest): Promise<SessionInfo> {
  await http.post(
    '/fo/auth/login',
    { username: req.email, password: req.password, rememberMe: req.rememberMe ?? false },
    withErrors({
      silentStatuses: [401, 429],
      silentWhen: (e) => e.status === 403 && e.message.includes(PENDING_LOGIN_MESSAGE_MARKER),
    }),
  );
  return fetchSession();
}

/** 로그아웃(FR-012) — 스토어 정리는 useAuthStore.logout 이 담당. 이미 만료된 세션(401)은 무음. */
export async function logout(): Promise<void> {
  await http.delete('/fo/auth/oauth2/logout', withErrors({ silentStatuses: [401] }));
}

/** 로컬 가입(FR-013) — PENDING 생성·세션 미발급(인증 메일 발송 흐름). 409(아이디·이메일 중복)는 화면 인라인. */
export async function signup(req: LocalSignupRequest): Promise<{ email: string }> {
  const { data } = await http.post<{ username: string; email: string }>(
    '/fo/auth/signup',
    req,
    withErrors({ silentStatuses: [409] }),
  );
  return { email: data.email };
}

/** 소셜 핸드오프 peek — 비소진. 404 = 만료·무효(화면이 만료 안내로 흡수). */
export async function peekSocialHandoff(handoffToken: string): Promise<HandoffContext> {
  const { data } = await http.get<HandoffContext>(
    `/fo/auth/signup/social/context/${encodeURIComponent(handoffToken)}`,
    withErrors({ silentStatuses: [404] }),
  );
  return data;
}

/**
 * 소셜 가입 — 검증 이메일이면 VERIFIED 즉시 생성 + 세션 쿠키 발급(세션 확인으로 SessionInfo 구성),
 * 아니면 PENDING 생성 + 인증 메일 발송(세션 없음). 409(이메일·소셜 중복)는 화면 인라인.
 */
export async function signupSocial(req: SocialSignupRequest): Promise<SocialSignupResult> {
  const { data } = await http.post<SignupResponse>(
    '/fo/auth/signup/social',
    req,
    withErrors({ silentStatuses: [409] }),
  );
  if (data.authStatus === 'PENDING') return { status: 'PENDING', email: data.email };
  return { status: 'VERIFIED', session: await fetchSession() };
}

/** 인증 대기(PENDING 소셜 회원) 정보 — 인증 메일이 간 현재 이메일. 404 = 토큰 만료·이미 인증 완료. */
export async function getPendingVerification(token: string): Promise<PendingVerificationResponse> {
  const { data } = await http.get<PendingVerificationResponse>(
    `/fo/auth/verify-email/pending/${encodeURIComponent(token)}`,
    withErrors({ silentStatuses: [404] }),
  );
  return data;
}

/** 인증 대기 회원 인증 메일 재발송 — 60초 쿨다운 429·토큰 만료 404 는 화면이 흡수. */
export async function resendPendingVerification(token: string): Promise<void> {
  await http.post(
    `/fo/auth/verify-email/pending/${encodeURIComponent(token)}/resend`,
    undefined,
    withErrors({ silentStatuses: [404, 429] }),
  );
}

/** 인증 대기 회원 이메일 정정 — 새 주소로 인증 메일 발송. 409(사용 중)·429·404 는 화면 인라인. */
export async function changePendingEmail(token: string, email: string): Promise<void> {
  await http.post(
    `/fo/auth/verify-email/pending/${encodeURIComponent(token)}/email`,
    { email } satisfies ChangePendingEmailRequest,
    withErrors({ silentStatuses: [404, 409, 429] }),
  );
}

/** 인증 메일 재발송(FR-015) — 계정 존재와 무관하게 200. 60초 쿨다운 429 는 카운트다운 UI 로 흡수. */
export async function resendVerification(email: string): Promise<void> {
  await http.post('/fo/auth/verify-email/resend', { email }, withErrors({ silentStatuses: [429] }));
}

/** 이메일 인증(FR-014) — 세션을 만들지 않는다(사용자가 직접 로그인). 실패는 400 단일 — 화면이 만료 카드로 흡수. */
export async function verifyEmail(token: string): Promise<{ email: string }> {
  const { data } = await http.post<{ email: string }>(
    '/fo/auth/verify-email',
    { token },
    withErrors({ silentStatuses: [400] }),
  );
  return data;
}

/** 재설정 메일 요청(FR-015) — 계정 존재와 무관하게 200. 60초 쿨다운 429 는 카운트다운 UI 로 흡수. */
export async function requestPasswordReset(email: string): Promise<void> {
  await http.post(
    '/fo/auth/password-reset/request',
    { email },
    withErrors({ silentStatuses: [429] }),
  );
}

/** 재설정 토큰 사전 검증(비소모) — 실패는 400 단일("링크가 만료되었거나 유효하지 않습니다."). */
export async function verifyResetToken(token: string): Promise<void> {
  await http.post(
    '/fo/auth/password-reset/verify',
    { token },
    withErrors({ silentStatuses: [400] }),
  );
}

/**
 * 비밀번호 변경(토큰 소비) — 400 은 두 갈래(정책 위반 메시지 / 토큰 만료 단일 메시지)를 화면이 메시지 마커로 구분한다.
 * 확인값 일치는 화면 스키마가 이미 검증했으므로 같은 값을 확인 필드로 보낸다.
 */
export async function resetPassword(token: string, password: string): Promise<void> {
  await http.post(
    '/fo/auth/password-reset/confirm',
    { token, newPassword: password, newPasswordConfirm: password },
    withErrors({ silentStatuses: [400] }),
  );
}

/**
 * 소셜 SSO 시작 URL(FR-017·specs/033) — 브라우저 전체 이동(XHR 아님). 복귀지는 호출부가 이동 직전 returnUrl 로 보존하고,
 * 백엔드 콜백은 `/auth/callback`(기가입)·`/signup?handoffToken=`(신규)·`/verify-email/pending?token=`(인증 대기)으로 돌아온다.
 */
export function socialAuthorizeUrl(provider: SocialProvider, rememberMe = false): string {
  // "로그인 상태 유지"(specs/048)는 백엔드가 state 에 실어 콜백까지 운반한다.
  const path = `/api/fo/auth/oauth2/authorize/${provider}${rememberMe ? '?remember=true' : ''}`;
  const base = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (!base || base === '/api') return path;
  return `${base.replace(/\/api\/?$/, '')}${path}`;
}
